/** Private Worker coordinator → task-executor transport. Never included in public runner state. */
import { Schema } from "effect"
import { ProfileId } from "./ids"
import { JobSpec, SignedJob } from "./job"
import {
  PlatformAgent,
  PlatformCodexTokens,
  PlatformExecutionProfile,
  PlatformGithubCredential,
  PlatformRuntime,
} from "./platform"
import { ActiveLease, ConsoleProfileWish, ReportBody } from "./rpc/hub"
import { Lease, LeaseRef, RunError, RunHead, SessionRef, StopReason, Usage } from "./run"
import {
  ConsoleModelInfo,
  ProfileAccess,
  ProfileCliSettings,
  ProfileScope,
  ProfileUsage,
  RunnerManifest,
} from "./runner"
import { RunFunding } from "./usage"

export const PlatformExecutionStart = Schema.Struct({
  pairing: Schema.Struct({ workspaceId: Schema.String, runnerId: Schema.String, server: Schema.String }),
  lease: Lease,
  job: JobSpec,
  profile: Schema.Struct({
    id: Schema.String,
    agent: PlatformAgent,
    cli: ProfileCliSettings,
    codexReasoningSummary: Schema.optionalKey(
      Schema.Literals(["inherit", "none", "auto", "detailed", "concise"]),
    ),
    account: Schema.optionalKey(
      Schema.Struct({
        authMethod: Schema.optionalKey(Schema.Literals(["subscription", "api_key", "unknown"])),
        plan: Schema.optionalKey(Schema.String),
      }),
    ),
  }),
  repo: Schema.NullOr(Schema.Struct({ id: Schema.String, remote: Schema.String })),
})
export type PlatformExecutionStart = typeof PlatformExecutionStart.Type
export const PlatformExecutionTick = Schema.Struct({
  ack: Schema.Number.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(0)),
  remainingMs: Schema.Number.check(
    Schema.isFinite(),
    Schema.isGreaterThanOrEqualTo(0),
    Schema.isLessThanOrEqualTo(15_000),
  ),
  /**
   * Decision 0150: how long the hub's lease has left when the tick was sent. An image with `platform_fence_at_expiry`
   * fences at this minus PLATFORM_FENCE_MARGIN_MS (never earlier than `remainingMs`); an older image ignores the key
   * and fences on `remainingMs`. An older controller does not send it, and a newer image then fences on `remainingMs`.
   */
  leaseRemainingMs: Schema.optionalKey(
    Schema.Number.check(Schema.isFinite(), Schema.isGreaterThanOrEqualTo(0)),
  ),
  mcpTokens: Schema.Array(
    Schema.Struct({ name: Schema.String, token: Schema.String, expiresAt: Schema.Number }),
  ),
  streamToken: Schema.String,
  lost: Schema.NullOr(Schema.Literals(["expired", "stale_epoch", "revoked", "self_fenced", "shutdown"])),
})
/**
 * Decision 0150: a task sandbox fences this long before its lease expires, so the hub never sees two executions of one
 * run (the tick's transit time and clock drift are inside the margin).
 */
export const PLATFORM_FENCE_MARGIN_MS = 5_000
/**
 * Decision 0155: the environment variable that tells a task sandbox (`platform_tool_priority`) the nice value for every
 * process but its task server; the Worker sets it to PLATFORM_TOOL_NICE. Unset, 0 or unreadable: nothing is reniced.
 */
export const PLATFORM_TOOL_NICE_ENV = "MINGA_PLATFORM_TOOL_NICE"
/** Decision 0155: the nice value of a sandbox's tools (CFS weight 110 against the task server's 1024 at nice 0). */
export const PLATFORM_TOOL_NICE = 10
/** Decision 0150: the one error code of a Platform run whose sandbox heard no tick until its fence. */
export const PLATFORM_TICK_LOST = "platform_tick_lost"
/**
 * Decision 0150: the deadline a sandbox fences at, from a tick received at `now`: the lease's expiry minus the margin
 * when the controller sent it, else (an older controller) the plain `remainingMs` window of at most 15 s. A tick with
 * `remainingMs: 0` (a lost lease) fences at once.
 */
export const platformFenceDeadline = (
  now: number,
  tick: Pick<PlatformExecutionTick, "remainingMs" | "leaseRemainingMs">,
): number =>
  now +
  (tick.remainingMs === 0
    ? 0
    : tick.leaseRemainingMs === undefined
      ? tick.remainingMs
      : Math.max(0, tick.leaseRemainingMs - PLATFORM_FENCE_MARGIN_MS))
export type PlatformExecutionTick = typeof PlatformExecutionTick.Type
export const PlatformMechanicalEnd = Schema.Struct({
  stopReason: StopReason,
  usage: Schema.Array(Usage),
  sessionRef: Schema.NullOr(SessionRef),
  error: Schema.NullOr(RunError),
  streamLastFrameSeq: Schema.Number,
  head: Schema.optionalKey(RunHead),
  funding: Schema.optionalKey(RunFunding),
  /** Decision 0149: the sandbox's resources during the run (`RunResources`, sanitized by the hub). */
  resources: Schema.optionalKey(Schema.Json),
})
export const PlatformExecutionState = Schema.Struct({
  runtime: Schema.optionalKey(PlatformRuntime),
  fenced: Schema.optionalKey(Schema.Boolean),
  observation: Schema.optionalKey(
    Schema.Struct({
      models: Schema.Array(ConsoleModelInfo),
      defaultModel: Schema.optionalKey(Schema.String),
      accountKey: Schema.optionalKey(Schema.String),
      plan: Schema.optionalKey(Schema.String),
      checkedAt: Schema.String,
      validated: Schema.Boolean,
      health: Schema.Literals(["ok", "logged_out", "error", "unknown"]),
    }),
  ),
  profileUsage: Schema.optionalKey(Schema.NullOr(ProfileUsage)),
  renewRequested: Schema.optionalKey(Schema.Boolean),
  /**
   * Decision 0155 (`platform_tool_priority`): the sandbox's own side of this tick: the gap since the previous tick
   * arrived, the longest event-loop stall of its task server since then, how long it took to answer, and the sandbox's
   * CPU (% of its quota, when sampled). Next to the Worker's round trip it tells a starved sandbox from a slow path.
   */
  tick: Schema.optionalKey(
    Schema.Struct({
      gapMs: Schema.Number,
      loopLagMs: Schema.Number,
      handleMs: Schema.Number,
      cpuPct: Schema.optionalKey(Schema.Number),
    }),
  ),
  reports: Schema.Array(Schema.Struct({ seq: Schema.Number, body: ReportBody })),
  end: Schema.NullOr(PlatformMechanicalEnd),
  failed: Schema.NullOr(
    Schema.Struct({ reason: Schema.Literals(["ceiling", "prepare_failed"]), detail: Schema.String }),
  ),
})
export type PlatformExecutionState = typeof PlatformExecutionState.Type
export const PlatformTaskBootstrap = Schema.Struct({
  start: PlatformExecutionStart,
  profile: PlatformExecutionProfile,
  claudeToken: Schema.optionalKey(Schema.String),
  codexTokens: Schema.optionalKey(PlatformCodexTokens),
  authUrl: Schema.String,
  authToken: Schema.String,
  githubToken: Schema.optionalKey(Schema.String),
  githubCredential: Schema.optionalKey(PlatformGithubCredential),
  githubAuthUrl: Schema.optionalKey(Schema.String),
})
export type PlatformTaskBootstrap = typeof PlatformTaskBootstrap.Type

/** Reviews keep the existing distinct worktree key. Agent turns have an explicit persistent scope. */
export const platformTaskScope = (
  job: Pick<PlatformExecutionStart["job"], "kind" | "workspace" | "runId" | "projectId"> & {
    taskId?: PlatformExecutionStart["job"]["taskId"] | null
    agentId?: PlatformExecutionStart["job"]["agentId"] | null
  },
): string => {
  const project = job.projectId ?? "workspace"
  if (job.taskId)
    return `${project}/${job.kind}/${job.workspace._tag === "worktree" ? (job.workspace.worktreeKey ?? job.taskId) : job.taskId}`
  if (job.agentId) return `${project}/agent/${job.agentId}`
  return `${project}/run/${job.runId}`
}

/** Trusted Worker coordinator → authoritative workspace hub messages. */
export const PlatformHubConnect = Schema.Struct({
  manifest: RunnerManifest,
  active: Schema.Array(ActiveLease),
  /** Decision 0128: each profile's subscription owner and scope (absent from an older coordinator = none). */
  access: Schema.optionalKey(Schema.Record(ProfileId, ProfileAccess)),
})
export const PlatformHubClaim = Schema.Struct({ lease: Lease, job: SignedJob })
export const PlatformHubReport = Schema.Struct({
  lease: LeaseRef,
  seq: Schema.Number.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(1)),
  at: Schema.Number,
  body: ReportBody,
})
export const PlatformHubConfig = Schema.Struct({
  rev: Schema.Number,
  capacity: Schema.NullOr(Schema.Number),
  autoUpdate: Schema.NullOr(Schema.Boolean),
  profiles: Schema.Array(ConsoleProfileWish),
})
export const PlatformHubSettings = Schema.Struct({
  repos: Schema.Array(
    Schema.Struct({ repoId: Schema.String, remote: Schema.String, defaultBranch: Schema.String }),
  ),
  config: Schema.NullOr(PlatformHubConfig),
  /**
   * Decision 0167 §4: each account's sharing as the workspace stores it (owner and account key), so a Platform
   * subscription of an account with a setting takes it. Absent from a Workspace DO before 0167.
   */
  accounts: Schema.optionalKey(
    Schema.Array(
      Schema.Struct({
        ownerUserId: Schema.String,
        accountKey: Schema.String,
        scope: ProfileScope,
        projectIds: Schema.Array(Schema.String),
      }),
    ),
  ),
})
export const PlatformHubRenewal = Schema.Struct({
  expiresAt: Schema.Number,
  mcpTokens: PlatformExecutionTick.fields.mcpTokens,
  streamToken: Schema.String,
  lost: PlatformExecutionTick.fields.lost,
})
